import {  Check, Circle, CircleChevronDown, Clock } from "lucide-react";
import React, { useEffect } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@workspace/web-ui/components/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@workspace/web-ui/components/avatar";
import { Badge } from "@workspace/web-ui/components/badge";
import { Button } from "@workspace/web-ui/components/button";
import { Card, CardContent } from "@workspace/web-ui/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";
import ConsultationClock from "~/assets/clock.svg"
import { useSuspenseQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { usePaymentCheckoutMutation } from "~/features/payments/api/mutations";
import { useAuth } from "~/providers/auth.provider";
import { EmptyTodo, EmptyCompletedTodo } from "../../components/empty-todo";
import SnapshotCards from "../../components/snapshot-card";
import SubNav from "../../components/sub-nav";
import { accordionItems } from "../../data/accordion-data";
import { activeMedicationsQueryOptions, medicalsAllergiesQueryOptions, medicalsConditionsQueryOptions } from "../medicals/api/queries";
import { tasksQueryOptions } from "./api/queries";
import { groupTasksByMonth, getTasksWithReadableMonths } from "./lib/task";
import { InputData, Task, MonthData } from "./types";
import { MedicationRefillRequestModal } from "../../components/medication-request";
import { MedicationPaymentModal } from "../../components/medication-payment";
import { MedicationRefillPaymentModal } from "../../components/medication-refill-payment";
import { formatFullDate, formatTime } from "@workspace/lib/date";
// import { useCreateRegistrationPaymentMutation } from "~/features/appointments/api/mutations/consultation";
import { useTaskStore } from "../../store/task";
// import { formatDate } from "~/lib/date-utils";
import { useNavigate } from "@tanstack/react-router";
// import { mockUserTasksMultiMonth } from "./data";

export function WeightLossPage() {
  const tasksQuery = useSuspenseQuery(tasksQueryOptions());
  const medicalsAllergiesQuery = useSuspenseQuery(medicalsAllergiesQueryOptions("weight_loss"));
  const medicalsConditionsQuery = useSuspenseQuery(medicalsConditionsQueryOptions("weight_loss"));
  const activeMedicationsQuery = useSuspenseQuery(activeMedicationsQueryOptions());

  const paymentCheckoutMutation = usePaymentCheckoutMutation();

  const router = useRouter()

  // const taskStatus = (mockUserTasksMultiMonth || {}) as InputData;
  const taskStatus = (tasksQuery.data?.task_status || {}) as InputData;

  // console.log("WeightLossPage", tasksQuery.data?.task_status)
  // const auth = useAuth();
  const { user } = useAuth();
  const [payForMed, setPayForMed] = React.useState(false);
  const [payForRefill, setPayForRefill] = React.useState(false);
  const [requestRefill, setRequestRefill] = React.useState(false);
  const [activeItem, setActiveItem] = React.useState("");
  const [activeTodoTask, setActiveTodoTask] = React.useState("");
  const [activeCompletedTask, setActiveCompletedTask] = React.useState("");

  const [prescriptionId, setPrescriptionId] = React.useState("");
  const [reqPrescriptionId, setReqPrescriptionId] = React.useState("");
  const [reqMedicationId, setReqMedicationId] = React.useState("");
  const [refillPrescriptionId, setRefillPrescriptionId] = React.useState("");
  const [refillMedicationId, setRefillMedicationId] = React.useState("");
  // const { mutate: createRegistrationPayment, isPending } = useCreateRegistrationPaymentMutation();
  const navigate = useNavigate()
  const {setTaskCount} = useTaskStore()

  // Group tasks by month and get them in readable format
  const groupedTasks = groupTasksByMonth(taskStatus);
  const tasksWithReadableMonths = getTasksWithReadableMonths(groupedTasks);

  const sortedMonths: MonthData[] = Object.entries(tasksWithReadableMonths)
    .sort((a, b) => {
      const monthA = new Date(a[0]).getTime();
      const monthB = new Date(b[0]).getTime();
      return monthA - monthB;
    })
    .map(([monthName, tasks]) => ({
      month: monthName,
      data: tasks
    }));

    useEffect(() => {
      setTaskCount(sortedMonths.reduce((total, monthData) => {
        return total + monthData.data.filter((task: Task) => !task.completed && !(task.type === "Consultation" && task.review_status === "in_review")).length;
      }, 0))
    }, [sortedMonths.length, setTaskCount])

  const completedStages = sortedMonths.map(monthData =>
    monthData.data.every((task: Task) => task.completed)
  );

  const successUrl = (appointment_id: string) => `${window.location.origin}/dashboard/weight-loss/appointments/confirmation?appointment_id=${appointment_id}`;
  const errorUrl = (appointment_id: string) => `${window.location.origin}/dashboard/weight-loss/appointments/error?appointment_id=${appointment_id}`;
  // const consultSuccessUrl = `${window.location.origin}/consultations/confirmation`;
  // const consultCancelUrl = `${window.location.origin}/consultations/error`;

  function formatAppointmentText(text: string) {
    const monthName = new Date(user?.registration_date || "").toLocaleString('en-US', { month: 'long' });
    const formattedText = text.replace(/\(Month \d+\)/, `(${monthName})`);
    return formattedText;
  }

  const renderTaskActionButton = (task: Task) => {
    // Join appointment meeting
    if (task.type === "Appointment" && task.meeting_url && !task.reschedule && !task.payment_required) {
      return (
        <a
          href={task.meeting_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center w-full justify-center rounded-lg bg-[#4272DD] px-4 py-2 text-sm font-medium text-white hover:bg-[#3661C5]"
        >
          Join Meeting
        </a>
      );
    }

    // Reschedule appointment
    if (task.type === "Appointment" && task.reschedule && !task.payment_required) {
      return (
        <Button
          onClick={() => {
            router.navigate({ to: `/dashboard/weight-loss/appointments/reschedule-booking?appointment_id=${task.appointment_id}`});
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Reschedule
        </Button>
      );
    }

    // Book appointment
    if (task.type === "Appointment" && !task.meeting_url && !task.payment_required && !task.completed) {
      return (
        <Button
          onClick={() => {
            router.navigate({ to: `/dashboard/weight-loss/appointments/booking`, });
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Book now
        </Button>
      );
    }

    // pay for appointment
    if (task.type === "Appointment" && task.payment_required && !task.reschedule) {
      return (
        <Button
          onClick={() => {
            if (!task.completed && task.appointment_id) {
              paymentCheckoutMutation.mutate({
                payment_type: task.type,
                type_id: task.appointment_id,
                success_url: successUrl(task.appointment_id),
                cancel_url: errorUrl(task.appointment_id),
              }, {
                onSuccess: (data) => {
                  if (data.success) {
                    window.location.href = data.data.url;
                  }
                },
                onError: (error) => {
                  console.error(error);
                }
              });
            }
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
          disabled={paymentCheckoutMutation.isPending}
        >
          Pay now
        </Button>
      );
    }

        // Complete consultation
    if (task.type === "Consultation" && task.payment_required) {
      return(
      <Button
        onClick={() => {
          // createRegistrationPayment({
          //   name: `${auth?.user?.first_name} ${auth?.user?.last_name}`,
          //   email: auth?.user?.email,
          //   phone: auth?.user?.phone_number,
          //   success_url: consultSuccessUrl,
          //   cancel_url: consultCancelUrl,
          //   consultation_id: task.consultation_id,
          // }, {
          //   onSuccess: (response) => {
          //   if (response.success) {
          //     window.location.replace(response.data?.payment_metadata.url || '');
          //   }
          //   },
          //   onError: () => {
          //   // Error handling
          //   }
          // });
        navigate({ to: "/questionnaire" });
        }}
        className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        // disabled={isPending}
      >
        {/* {isPending ? (
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
        </div>
        ) : ( */}
        Pay now
        
      </Button>
      )
    }

    // pay for prescription
    if (task.type === "Prescription" && task.payment_required) {
      return (
        <Button
          onClick={() => {
            if (!task.completed && task.prescription_id) {
              setPrescriptionId(task.prescription_id)
              setPayForMed(true)
            }
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
Pay for medication
        </Button>
      );
    }

    // pay for refill
    if (task.type === "Refill" && task.payment_required) {
      return (
        <Button
          onClick={() => {
            console.log("Refill task", task)
            if (!task.completed && task.prescription_id) {
              setRefillPrescriptionId(task.prescription_id)
              setRefillMedicationId(task.medication_id)
              setPayForRefill(true)
            }
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Pay now
        </Button>
      );
    }

    // request for refill
    if (task.type === "Refill" && !task.payment_required) {
      return (
        <Button
          onClick={() => {
            if (!task.completed && task.prescription_id) {
              setReqPrescriptionId(task.prescription_id)
              setReqMedicationId(task.medication_id)
              setRequestRefill(true)
            }
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Request now
        </Button>
      );
    }

    if (task.type === "Medical_Plan" &&  task.completed === false) {
      return (
        <Button
          onClick={() => {
            navigate({to: "/dashboard/supplementary"})
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Get Started
        </Button>
      );
    }

    // Complete questionnaire
    if (task.type === "Questionnaire") {
      return (
        <Button
          onClick={() => {
            router.navigate({ to: '/dashboard/onboarding' });
          }}
          className="w-full sm:w-auto rounded-lg bg-[#4272DD]"
        >
          Complete now
        </Button>
      );
    }

    return null;
  };

const renderAccordionContent = (task: Task) => {
  if (task.completed) {
    // Content for completed tasks
    const Appointment = { 
      title:"Please ensure your microphone is ready and prepared for the meeting. Your punctuality is greatly appreciated; please join the meeting on time.",
      list:[
      { label: "Time", value:  "Today, 2:30 PM" },
      { label: "Type", value: "Follow-up" },
      { label: "Status", value: "Pending" },
  ]}
    const AppointmentWithReschedule = {
      title:"Reschedule your virtual consultation at a time that suits you.",
      list:[
        { label: "Type", value: "Weight Loss Consultation" },
        { label: "Duration", value:"30 minutes" },  
        { label: "Location", value: "Online" },  
        { label: "Fee", value: "--" },
     ]}
    // const AppointmentWithMeetingLink = { 
    //   title:"Please ensure your microphone is ready and prepared for the meeting. Your punctuality is greatly appreciated; please join the meeting on time.",
    //   list:[
    //   { label: "Time", value:  "Today, 2:30 PM" },
    //   { label: "Type", value: "Follow-up" },
    //   { label: "Status", value: "Pending" },
    // ]}

    const MedicalPlan = { 
      title:"Select your preferred medication type, choose your consultation, complete the consultation payment, and wait while your profile is reviewed by a doctor.",
      list:[
      { label: "Progress", value: "60% complete" },
      { label: "Next Step", value:  "Choose medication" },
     ]}
    const Consultation = { 
      title:"",
      list:[
      { label: "Dosage", value: "Once weekly" },
      { label: "Supply", value:  "4 weeks" },
      { label: "Total", value: "$89.99" },
    ]}
    const Prescription = { 
      title:"Semaglutide - 1mg",
      description:"Weekly injection for weight management. 4-week supply.",
      list:[
      { label: "Dosage", value: "Once weekly" },
      { label: "Supply", value:  "4 weeks" },
      { label: "Total", value: "$89.99" },
    ]}
    const Refill = { 
      title:"",
      list:[
      { label: "Dosage", value: "Once weekly" },
      { label: "Supply", value:  "4 weeks" },
      { label: "Total", value: "$89.99" },
    ]}

    return (
      <div className="space-y-4 w-full">
        <div className="space-y-2">
            {task.type === "Appointment"  && (<ListCointainer title={Appointment.title} list={Appointment.list} />)}
            {task.type === "Consultation" && (<ListCointainer title={Consultation.title} list={Consultation.list} />)}
            {(task.type === "Prescription" || task.type === "Refill" )&& (<ListCointainer title={Prescription.title} description={Prescription.description} list={Prescription.list} />)}
            {task.type === "Refill" && (<ListCointainer title={Refill.title} list={Refill.list} />)}
            {task.type === "Questionnaire" && (<ListCointainer title={Refill.title} list={Refill.list} />)}
            {(task.type === "Medical_Plan" && task.title === "Complete Health Questionnaire") && (<ListCointainer title={MedicalPlan.title} list={MedicalPlan.list} />)}
            {(task.type === "Medical_Plan" && task.title === "Complete Your Weight Loss Onboarding")  &&  (<ListCointainer title={Appointment.title} list={Appointment.list} />)}
            {(task.type === "Medical_Plan" && task.title === "Complete Supplementary Questionnaire") && (<ListCointainer title={AppointmentWithReschedule.title} list={AppointmentWithReschedule.list} />)}

          {/* <h3 className="text-base font-medium">What to expect next</h3>
          <ol className="list-decimal pl-5 space-y-1">
            {task.type === "Appointment" && (
              <>
                <li className="text-sm text-[#1a202c]">Review and implement the discussed recommendations or action items.</li>
                <li className="text-sm text-[#1a202c]">You will receive notifications and updates on your dashboard.</li>
                <li className="text-sm text-[#1a202c]">You can contact support if you have any questions or concerns.</li>
              </>
            )}
            {task.type === "Consultation" && (
              <>
                <li className="text-sm text-[#1a202c]">Our medical team will review your case and create a personalized plan.</li>
                <li className="text-sm text-[#1a202c]">You will receive notifications and updates on your dashboard.</li>
                <li className="text-sm text-[#1a202c]">You can contact support if you have any questions or concerns.</li>
              </>
            )}
            {(task.type === "Prescription" || task.type === "Refill") && (
              <>
                <li className="text-sm text-[#1a202c]">Your order is now being processed and will be shipped shortly.</li>
                <li className="text-sm text-[#1a202c]">You will receive notifications and updates on your dashboard.</li>
                <li className="text-sm text-[#1a202c]">You can contact support if you have any questions or concerns.</li>
              </>
            )}
            {(task.type === "Questionnaire") && (
                            <>
                <li className="text-sm text-[#1a202c]">Be prepared for a potential consultation or follow-up.</li>
                <li className="text-sm text-[#1a202c]">You will receive notifications and updates on your dashboard.</li>
                <li className="text-sm text-[#1a202c]">You can contact support if you have any questions or concerns.</li>
              </>
            )}
            {(task.type === "Medical_Plan" && task.title === "Complete Health Questionnaire") && (
                            <>
                            <li className="text-sm text-[#1a202c]">Be prepared for a potential consultation or follow-up.</li>
                            <li className="text-sm text-[#1a202c]">You will receive notifications and updates on your dashboard.</li>
                            <li className="text-sm text-[#1a202c]">You can contact support if you have any questions or concerns.</li>
                          </>
            )}
            {(task.type === "Medical_Plan" && task.title === "Complete Your Weight Loss Onboarding") && (
                            <>
                            <li className="text-sm text-[#1a202c]">You now need to complete a short supplementary questionnaire</li>
                            <li className="text-sm text-[#1a202c]">These questions give your doctpr a complete picture of your health, lifestyle, and goals</li>
                            <li className="text-sm text-[#1a202c]">You can contact support if you have any questions or concerns.</li>
                          </>
            )}
            {(task.type === "Medical_Plan" && task.title === "Complete Supplementary Questionnaire") && (
                            <>
                            <li className="text-sm text-[#1a202c]">You now need to complete a short supplementary questionnaire</li>
                            <li className="text-sm text-[#1a202c]">These questions give your doctpr a complete picture of your health, lifestyle, and goals</li>
                            <li className="text-sm text-[#1a202c]">You can contact support if you have any questions or concerns.</li>
                          </>
            )}
          </ol> */}
        </div>

        {/* Display additional information for completed tasks */}
        {/* {(task.type === "Consultation" || task.type === "Prescription" || task.type === "Refill" || task.title === "Complete Your Weight Loss Onboarding" || task.title === "Complete Supplementary Questionnaire") && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
            <div className="flex items-start gap-2">
              <Calendar className="size-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Date initiated</p>
                <p className="text-sm font-medium">{task.when ? formatDate(task.when) : "24 April, 2025"}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <DollarSign className="size-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Amount Paid:</p>
                <p className="text-sm font-medium">{"--"}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <FileText className="size-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">
                  {task.type === "Consultation" || task.type === "Appointment" || task.type == "Medical_Plan"
                    ? "Consultation Details:" 
                    : "Medications"}
                </p>
                <p className="text-sm font-medium">
                  {task.type === "Consultation" || task.type === "Appointment" || task.type == "Medical_Plan"
                    ? "Weight Loss Consultation" 
                    : "Ozempic, Vitamin C, Aderrall"}
                </p>
              </div>
            </div>
          </div>
        )} */}
        <div className=" flex w-full overflow-hidden">
          <p className="font-medium break-words lg:!text-[14px] !text-[12px] text-[#1A202C]">
            Contact Support: Need help? Reach out to our support team
             appointments@proxymedicine.com
          </p>
        </div>
      </div>
    );
  } else {
    // Content for pending tasks
    const Appointment = { 
      title:"Please ensure your microphone is ready and prepared for the meeting. Your punctuality is greatly appreciated; please join the meeting on time.",
      list:[
      { label: "Date", value: "24 April, 2025" },
      { label: "Time", value:  "10:00 AM" },
      { label: "Type", value: "Weight Loss Consultation" },
      { label: "Status", value: "Pending" },
      { label: "Duration", value:"30 minutes" },
      { label: "Location", value: "Online" },  
    ]}
    const AppointmentWithReschedule = {
      title:"Reschedule your virtual consultation at a time that suits you.",
      list:[
        { label: "Type", value: "Weight Loss Consultation" },
        { label: "Duration", value:"30 minutes" },  
        { label: "Location", value: "Online" },  
        { label: "Fee", value: "--" },
     ]}
    const AppointmentWithMeetingLink = { 
      title:"Please ensure your microphone is ready and prepared for the meeting. Your punctuality is greatly appreciated; please join the meeting on time.",
      list:[
      { label: "Time", value:  "Today, 2:30 PM" },
      { label: "Type", value: "Follow-up" },
      { label: "Status", value: "Pending" },
    ]}

    const MedicalPlan = { 
      title:"Select your preferred medication type, choose your consultation, complete the consultation payment, and wait while your profile is reviewed by a doctor.",
      list:[
      { label: "Progress", value: "60% complete" },
      { label: "Next Step", value:  "Choose medication" },
     ]}
    const Consultation = { 
      title:"",
      list:[
      { label: "Dosage", value: "Once weekly" },
      { label: "Supply", value:  "4 weeks" },
      { label: "Total", value: "$89.99" },
    ]}
    const Prescription = { 
      title:"Semaglutide - 1mg",
      description:"Weekly injection for weight management. 4-week supply.",
      list:[
      { label: "Dosage", value: "Once weekly" },
      { label: "Supply", value:  "4 weeks" },
      { label: "Total", value: "$89.99" },
    ]}
    const Refill = { 
      title:"",
      list:[
      { label: "Dosage", value: "Once weekly" },
      { label: "Supply", value:  "4 weeks" },
      { label: "Total", value: "$89.99" },
    ]}

    return (
      <div className="lg:gap-4 gap-2 flex flex-col w-full">
        {/* bg-[#0404040D] rounded-[8px] p-2 */}
        <div className=" ">
          {/* <h3 className="text-base font-medium">What to do</h3> */}
          {/* {task.type === "Medical_Plan" && (
                <p className="text-[#1A202C] font-medium">To move forward with your consultation, please answer a few additional questions:</p>
          )} */}
            {task.type === "Medical_Plan" && !task.completed  && (<ListCointainer title={MedicalPlan.title} list={MedicalPlan.list} />)}
            {task.type === "Consultation" && (<ListCointainer title={Consultation.title} list={Consultation.list} />)}
            {task.type === "Prescription" && (<ListCointainer title={Prescription.title} description={Prescription.description} list={Prescription.list} />)}
            {task.type === "Refill" && (<ListCointainer title={Refill.title} list={Refill.list} />)}
            {task.type === "Appointment" && task.meeting_url && (<ListCointainer title={AppointmentWithMeetingLink.title} list={AppointmentWithMeetingLink.list} />)}
            {task.type === "Appointment" && !task.meeting_url && !task.reschedule &&  (<ListCointainer title={Appointment.title} list={Appointment.list} />)}
            {task.type === "Appointment" && task.reschedule && (<ListCointainer title={AppointmentWithReschedule.title} list={AppointmentWithReschedule.list} />)}
            {/* <div>
            <h3 className="text-[16px] leading-[150%] text-[#1A202C] pb-[16px] font-medium">What to do</h3>
            <ol className=" bg-[#0404040D] rounded-[16px] border border-[#0404040D]">
              <ListItem Label="Date" value={task.when ? formatDate(task.when) : "24 April, 2025"} />
              <ListItem Label="Time" value="Weight Loss Consultation" />
              <ListItem Label="Type" value="Weight Loss Consultation" />
              <ListItem Label="Status" value="Weight Loss Consultation" />
              <ListItem Label="Duration" value="Weight Loss Consultation" />
              <ListItem Label="Location" value="Weight Loss Consultation" />
              <ListItem Label="Fee" value="Weight Loss Consultation" />
              <ListItem Label="Progress" value="Weight Loss Consultation" />  
              <ListItem Label="Next step" value="Weight Loss Consultation" />
              <ListItem Label="Questions" value="Weight Loss Consultation" />
              <ListItem Label="Estimated Time" value="Weight Loss Consultation" />
              <ListItem Label="Required" value="Weight Loss Consultation" />
              <ListItem Label="Dosage" value="Weight Loss Consultation" />
              <ListItem Label="Supply" value="Weight Loss Consultation" />
              <ListItem Label="Total" value="Weight Loss Consultation" />
            </ol>
          </div> */}
          {/* <ol className="list-decimal pl-5 space-y-1">
            {task.type === "Consultation" && task.payment_required && (
              <>
                <li className="text-sm text-[#1a202c]">Pay now to start your consultation.</li>
                <li className="text-sm text-[#1a202c]">Our team will review your case and create a personalized plan.</li>
                <li className="text-sm text-[#1a202c]">You'll receive updates on your dashboard.</li>
              </>
            )}
            {task.type === "Consultation" && !task.completed && task.review_status === "in_review" && (
              <>
                <li className="text-sm text-[#1a202c]">Your consultation is currently under review by our medical team.</li>
                <li className="text-sm text-[#1a202c]">We will notify you with updates and the next steps on your dashboard.</li>
              </>
            )}
            {task.type === "Medical_Plan" && (
              <>
                <li className="text-sm text-[#1a202c]">Click the "Get Started" button to begin the questionnaire</li>
                <li className="text-sm text-[#1a202c]">Review your answers carefully before submitting</li>
                <li className="text-sm text-[#1a202c]">Click "Submit" to complete the task</li>
              </>
            )}
            {task.type === "Prescription" && (
              <>
                <li className="text-sm text-[#1a202c]">Complete your payment for the medication order to proceed.</li>
                <li className="text-sm text-[#1a202c]">You'll receive updates on your dashboard.</li>
              </>
            )}
            {task.type === "Refill" && (
              <>
                <li className="text-sm text-[#1a202c]">Complete your payment for the medication refill order to proceed.</li>
                <li className="text-sm text-[#1a202c]">You'll receive updates on your dashboard.</li>
              </>
            )}
            {task.type === "Appointment" && task.meeting_url && (
              <>
                <li className="text-sm text-[#1a202c]">Review any provided materials and come prepared with questions or topics to discuss</li>
                <li className="text-sm text-[#1a202c]">Click the meeting link or dial-in number to join at the scheduled time</li>
              </>
            )}
            {task.type === "Appointment" && !task.meeting_url && !task.reschedule && (
              <>
                <li className="text-sm text-[#1a202c]">Select a date and time that works for you</li>
                <li className="text-sm text-[#1a202c]">Confirm your booking details</li>
              </>
            )}
            {task.type === "Appointment" && task.reschedule && (
              <>
                <li className="text-sm text-[#1a202c]">Review any provided materials and come prepared with questions or topics to discuss</li>
                <li className="text-sm text-[#1a202c]">Click the meeting link or dial-in number to join at the scheduled time</li>
              </>
            )}
          </ol> */}
        </div>
        
        <div className=" flex w-full overflow-hidden">
          <p className="font-medium break-words lg:!text-[14px] !text-[12px] text-[#1A202C]">
            Contact Support: Need help? Reach out to our support team
             appointments@proxymedicine.com
          </p>
        </div>
      </div>
    );
  }
};

  return (
    <main className="flex flex-col gap-6">
      <section className="py-10">
        <div className="w-full flex flex-col gap-[12px]">
                   {/* Greetings */}
        <span className="inline-flex gap-2">
          <h6 className="text-[16px] font-normal">Welcome,</h6>
          <h6 className="text-[16px] font-bold">{user?.first_name}</h6>
        </span>
        {/* Subnav one */}

        <SubNav
          title="Home"
          buttonOne="Go to urgent care "
          // buttonTwo="Book an appointmen"
        />
        {/* Subnav Two - Accordion */}

        <Accordion
          type="single"
          collapsible
          value={activeItem}
          onValueChange={setActiveItem}
          className="w-full "
        >
          <AccordionItem
            value="user-details"
            style={{boxShadow: "0px 0px 1px 0px rgba(0, 0, 0, 0.03), 0px 2px 2px 0px rgba(0, 0, 0, 0.03), 0px 4px 2px 0px rgba(0, 0, 0, 0.02), 0px 7px 3px 0px rgba(0, 0, 0, 0), 0px 11px 3px 0px rgba(0, 0, 0, 0)"}}
            className="mb-5 w-full rounded-2xl border-[1px] border-[#DCDCDE] bg-white p-3 text-black shadow-gray-500"
          >

            <AccordionTrigger className="flex w-full justify-start gap-3 py-0 hover:no-underline lg:flex-row  lg:justify-between [&>svg:last-child]:hidden">
              <div className="flex flex-col lg:gap-4 gap-1 flex-1 lg:w-auto w-full lg:flex-row lg:items-center lg:justify-start">

                {/* Top Row: Avatar + Username */}
                <div className="flex justify-between lg:w-auto w-full gap-3">
                <div className="flex items-center justify-start gap-3">
                  <Avatar className="size-8">
                    <AvatarImage src={user?.avatar || ""} />
                    <AvatarFallback>{`${user?.first_name?.charAt(0)}${user?.last_name?.charAt(0)}`}</AvatarFallback>
                  </Avatar>
                  <h6 className="text-base font-medium leading-[130%] text-[#2D2F35] lg:text-[18px]">{user?.first_name}{" "} {user?.last_name}</h6>
                  </div>
                  <CircleChevronDown
                strokeWidth={0.75}
                className="size-8 lg:hidden block text-[#7F8493] transition-transform duration-300 data-[state=open]:rotate-180"
              />

                  </div>

                {/* Stats go under username on mobile, inline on desktop */}
                <div className="flex flex-wrap justify-start  sm:items-center sm:gap-2 gap-2 ">
                  <span className="inline-flex gap-1 text-xs sm:text-sm">
                    <h6 className="font-normal flex items-center gap-1">Current <span className="lg:block hidden">Weight</span>:</h6>
                    <h6 className="font-medium sm:text-sm text-xs">{user?.health_profile?.weight} lbs</h6>
                  </span>
                  <div className="hidden sm:block h-[8px] w-[8px] rounded-full bg-[#0404041A]"></div>
                  <span className="inline-flex gap-1 text-xs sm:text-sm ">
                    <h6 className="font-normal flex items-center gap-1">Goal <span className="lg:block hidden">Weight</span>:</h6>
                    <h6 className="font-medium sm:text-sm text-xs">{user?.health_profile?.target_weight} lbs</h6>
                  </span>
                  <div className="hidden sm:block h-[8px] w-[8px] rounded-full bg-[#0404041A]"></div>

                  <span className="inline-flex gap-1 text-xs sm:text-sm ">
                    <h6 className="font-normal">Time Frame:</h6>
                    <h6 className="font-medium sm:text-sm text-xs">{user?.health_profile?.timeframe}</h6>
                  </span>
                </div>
              </div>

              {/* Chevron Icon – aligns right on desktop, centers on mobile */}
     <CircleChevronDown
                strokeWidth={0.75}
                className="size-8 lg:block hidden text-[#7F8493] transition-transform duration-300 data-[state=open]:rotate-180"
              />

             </AccordionTrigger>
            <AccordionContent className="w-full lg:p-2 mt-3 p-0">
              <SnapshotCards
                conditions={medicalsConditionsQuery.data?.conditions
                  ?.map((condition) => condition?.name)
                  .join(", ") || ""}
                allergies={medicalsAllergiesQuery.data?.allergies
                  ?.map((allergy) => allergy?.name)
                  .join(", ") || ""}
                activeMeds={activeMedicationsQuery.data?.active_medications
                  ?.map((activeMed) => activeMed?.medication?.name)
                  .join(", ") || ""}
                refills={accordionItems.content.medicalSnapshot.refills}
                avgWeekLoss={accordionItems.content.wellnessStats.avgWeekLoss}
                workouts={accordionItems.content.wellnessStats.workouts}
                calorieBudget={
                  accordionItems.content.wellnessStats.calorieBudget
                }
                target={accordionItems.content.wellnessStats.target}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        </div>

        {/* Timeline */}
        <div className="space-y-8">
          {sortedMonths.map((monthData, monthIndex) => (
            <div key={monthData.month} className="flex flex-col sm:flex-row">
              {/* Timeline Indicator - Hidden on mobile */}
              <div className="relative hidden w-10 flex-col items-center sm:flex">
                {completedStages[monthIndex] ? (
                  <Circle className="h-6 w-6 text-green-500" />
                ) : (
                  <>
                    <div className="relative flex size-10 items-center justify-center">
                      <Circle
                        className={`absolute size-7 ${monthIndex === 0 || completedStages[monthIndex - 1]
                          ? "text-[#4272DD]"
                          : "text-[#DCDCDE]"
                          }`}
                      />
                      <Circle
                        strokeWidth={5}
                        className={`absolute size-3 ${monthIndex === 0 || completedStages[monthIndex - 1]
                          ? "text-[#4272DD]"
                          : "text-[#DCDCDE]"
                          }`}
                      />
                    </div>

                    <div
                      className={`-mt-2 h-full w-1 ${monthIndex === 0 ? "bg-gradient-to-b from-blue-500 to-transparent" : "bg-gradient-to-b from-gray-300 to-transparent opacity-40"}`}
                    ></div>
                  </>

                )}
                {monthIndex < sortedMonths.length - 1 && (
                  <div
                    className={`-mt-2 h-full w-1 ${monthIndex === 0
                      ? "bg-gradient-to-b from-[#4272DD] to-transparent"
                      : "bg-gradient-to-b from-[#DCDCDE] to-transparent opacity-40"
                      }`}
                  ></div>
                )}
              </div>

              {/* Card Section */}
              <Card className="w-full border-[#4272DD] border rounded-[24px]">
                <CardContent className="p-0">
                  <div className="flex items-start justify-between gap-2 border-b border-b-[#ECECED] lg:px-6 px-4 pb-4 pt-3 sm:flex-row sm:items-center">
                    <h2 className="text-lg text-[#1A202C] font-medium">Month <span className=" font-normal">({monthData.month})</span> </h2>
                    <Badge
                      className={`rounded-2xl text-sm hover:bg-[#E1FAEA] ${completedStages[monthIndex]
                        ? "bg-[#E1FAEA] text-[#14AD4D]"
                        : "bg-[#F0F5FF] text-[#4272DD]"
                        }`}
                    >
                      {completedStages[monthIndex] ? "Completed" : "Ongoing"}
                    </Badge>
                  </div>

                  {/* Tabs */}
                  <Tabs defaultValue="to-do">
                    <TabsList className="flex h-12 w-full flex-wrap justify-start gap-4 rounded-none border-b border-b-[#ECECED] bg-background lg:px-6 px-4 py-0">
                      <TabsTrigger
                        value="to-do"
                        className="h-full rounded-none border-b-2 border-transparent bg-background px-0 py-4 text-sm data-[state=active]:border-[#4A85FF] data-[state=active]:shadow-none"
                      >
                        To-do
                        <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                          {monthData.data.filter((task: Task) => !task.completed && !(task.type === "Consultation" && task.review_status === "in_review")).length}
                        </Badge>
                      </TabsTrigger>
                      <TabsTrigger
                        value="completed"
                        className="h-full rounded-none border-b-2 border-transparent bg-background px-0 text-sm data-[state=active]:border-[#4A85FF] data-[state=active]:shadow-none"
                      >
                        Completed
                        <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                          {monthData.data.filter((task: Task) => task.completed).length}
                        </Badge>
                      </TabsTrigger>
                    </TabsList>

                    {/* To-do Tasks */}
                    <TabsContent value="to-do" className="lg:p-6 p-3">
                      {(() => {
                        const supplementaryQuestionnaireTask = monthData.data.find(task =>
                          task.type === "Medical_Plan" && task.title === "Complete Supplementary Questionnaire"
                        )

                        if (supplementaryQuestionnaireTask && !supplementaryQuestionnaireTask.completed) {
                          return (
                            <Accordion
                              type="single"
                              collapsible
                              value={activeTodoTask}
                              onValueChange={setActiveTodoTask}
                              className="w-full flex flex-col gap-4"
                              >
                              <AccordionItem
                                key={`todo-${monthData.month}-supplementary`}
                                value={`todo-${monthData.month}-supplementary`}
                                className="my-0 border !border-[#E6E6E6] rounded-[20px] shadow-sm"
                              >
                                <AccordionTrigger className="lg:p-4 p-2 relative hover:no-underline [&>svg:last-child]:hidden">
                                  <div className="flex w-full flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-2">
                                    <div className="flex items-start gap-3 flex-1">
                                      <Circle strokeWidth={2} className="lg:size-6 size-4 shrink-0 text-[#4272DD] mt-1 sm:mt-0" />
                                      <div className="flex text-left">
                                        <h3 className="lg:text-base text-sm font-medium break-words">
                                          {formatAppointmentText(supplementaryQuestionnaireTask.title)}
                                        </h3>
                                      </div>
                                    </div>
                                    <div className="flex w-full gap-2">
                                      {renderTaskActionButton(supplementaryQuestionnaireTask)}       
                                      <div>                                     
                                         <CircleChevronDown
                                          strokeWidth={0.75}
                                          className=" size-8  text-[#7F8493]  transition-transform duration-300 data-[state=open]:rotate-180"
                                        />
                                    </div>                            

                                    </div>
                                  </div>
                                  {/* absolute md:relative */}
                                  {/* <CircleChevronDown
                                      strokeWidth={0.75}
                                      className="size-8 hidden lg:block top-0 absolute md:relative mt-3 md:mt-0 md:ml-2 right-0 mr-2 md:mr-0 text-[#7F8493]  transition-transform duration-300 data-[state=open]:rotate-180"
                                    /> */}
                                </AccordionTrigger>
                                <AccordionContent className="lg:p-4 p-2">
                                <div className="flex">
                                  {renderAccordionContent(supplementaryQuestionnaireTask)}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          )
                        } else {
                          return (
                            <>
                              {/* Find the Consultation task in review */} 
                              {monthData.data.some(task => task.type === "Medical_Plan" && task.review_status === "in_review") ? (
                                <div className="flex flex-col items-center justify-center p-6 space-y-4 bg-white rounded-lg text-center">
                                  <div className="flex size-[60px] items-center justify-center rounded-full bg-[#F0F5FF]">
                                    {/* Placeholder for the icon */} 
                                    {/* <Clock size={32} className="text-blue-600" /> */}
                                    <img src={ConsultationClock} alt="clock" />
                                  </div>
                                  <h3 className="text-xl font-semibold text-gray-800">Consultation in Progress</h3>
                                  <p className="text-gray-600">
                                    Your consultation is being reviewed by our medical team.
                                    <br />
                                    You'll receive a personalized medical plan shortly.
                                  </p>
                                </div>
                              ) : (
                                <>
                                  {/* Filter for other To-do tasks (not completed and not the Consultation in review) */} 
                                  {monthData.data
                                     .filter((task: Task) =>
                                       !task.completed &&
                                       !(task.type === "Consultation" && task.review_status === "in_review") &&
                                       !(task.type === "Medical_Plan" && task.title === "Complete Supplementary Questionnaire") // Exclude the supplementary questionnaire task if it's completed or already handled
                                     )
                                     .length > 0 ? (
                                        <Accordion
                                          type="single"
                                          collapsible
                                          value={activeTodoTask}
                                          onValueChange={setActiveTodoTask}
                                          className="w-full flex flex-col gap-4"
                                        >
                                          {monthData.data
                                            .filter((task: Task) =>
                                              !task.completed &&
                                              !(task.type === "Consultation" && task.review_status === "in_review") &&
                                              !(task.type === "Medical_Plan" && task.title === "Complete Supplementary Questionnaire") // Exclude the supplementary questionnaire task if it's completed or already handled
                                            )
                                            .map((task: Task, index) => (
                                              <AccordionItem
                                                key={`todo-${monthData.month}-${index}`}
                                                value={`todo-${monthData.month}-${index}`}
                                                className=" border rounded-[20px] w-full overflow-hidden shadow-sm"
                                              >
                                                <AccordionTrigger className="!p-0 flex justify-between w-full  relative hover:no-underline [&>svg:last-child]:hidden">
                                                  <div className="lg:p-4 border-b border-b-[#0404041A] p-2 flex w-full flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-2">
                                                  <div className="flex lg:flex-row flex-col items-start justify-between lg:gap-3 gap-2 flex-1">
                                                       <div className="flex items-start gap-3 lg:w-fit justify-between w-full">
                                                  <Circle
                                                        strokeWidth={2}
                                                        className="lg:size-6 size-4 shrink-0 text-[#4272DD] mt-1 sm:mt-0"
                                                      />
                                                                                                                {task.priority && (
                                                        <Badge className="h-6 w-16 lg:hidden flex rounded-3xl bg-[#FFF1DA] text-[#9D6508] hover:bg-[#FFF1DA]">
                                                          Priority
                                                        </Badge>
                                                      )}
                                                  </div>
                                                      <div className="flex items-center lg:gap-3 gap-2 flex-1">

                                                      <div className="flex flex-col  w-full gap-1 text-left">
                                                        <h3 className="lg:text-base flex gap-2 items-center text-sm font-medium break-words">
                                                          {formatAppointmentText(task.title)}                                                       
                                                          {task.priority && (
                                                        <Badge className="h-6 w-16 hidden sm:flex rounded-3xl bg-[#FFF1DA] text-[#9D6508] hover:bg-[#FFF1DA]">
                                                          Priority
                                                        </Badge>
                                                      )}
                                                        </h3>
                                                        {/* {task.type === "Appointment" && task.meeting_url && !task.payment_required && task.time ? ( */}
                                                          <span className="text-sm text-[#2D2F35] flex items-center gap-2 flex-wrap">
                                                            <Clock size={16} className="text-[#7F8493]" />
                                                            <h6 className="text-xs text-[#202020CC]">
                                                              {`${formatFullDate(task.when || "")}`} {task.time ? `${formatTime(task?.time ?? "")}` : ""}
                                                            </h6>
                                                          </span>
                                                        {/* ) : null} */}
                                                      </div>
                                                    </div>
                                                    </div>
                                                    <div className="flex w-full lg:w-[18%] gap-2 justify-normal lg:justify-end">
                                                    {renderTaskActionButton(task)}
                                                    
                                                    <div>                                                    
                                                       <CircleChevronDown
                                                          strokeWidth={0.75}
                                                          className=" !size-8 text-[#7F8493]  transition-transform duration-300 data-[state=open]:rotate-180"
                                                        />
                                                    </div>
                                                    </div>
                                                  </div>
                                                  {/* absolute md:relative */}
                                                  {/* <CircleChevronDown
                                                    strokeWidth={0.75}
                                                    className="size-8 hidden lg:block top-0 absolute md:relative mt-3 md:mt-0 md:ml-2 right-0 mr-2 md:mr-0 text-[#7F8493]  transition-transform duration-300 data-[state=open]:rotate-180"
                                                  /> */}
                                                </AccordionTrigger>
                                                <AccordionContent className="lg:p-4 p-2 lg:bg-white bg-[#0404040D] ">
                                                  <div className="flex mt-2">
                                                    {renderAccordionContent(task)}
                                                  </div>
                                                </AccordionContent>
                                              </AccordionItem>
                                            ))}
                                        </Accordion>
                                      ) : <EmptyTodo />}
                                </>
                              )}
                            </>
                          )
                        }
                      })()}
                    </TabsContent>

                    {/* Completed Tasks */}
                    <TabsContent value="completed" className="lg:p-6 p-3">
                      {monthData.data
                        .filter((task: Task) => task.completed).length > 0 ? (
                          <Accordion
                            type="single"
                            collapsible
                            value={activeCompletedTask}
                            onValueChange={setActiveCompletedTask}
                            className="w-full flex flex-col overflow-hidden gap-4"
                            >
                            {monthData.data
                              .filter((task: Task) => task.completed)
                              .map((task: Task, index) => (
                                <AccordionItem
                                  key={`completed-${monthData.month}-${index}`}
                                  value={`completed-${monthData.month}-${index}`}
                                  className="my-0 border overflow-hidden rounded-[20px] shadow-sm "
                                >
                                  <AccordionTrigger className="lg:p-4 p-2 bg-[#EBFBF1] hover:no-underline [&>svg:last-child]:hidden">
                                    <div className="flex w-full justify-between items-center">
                                      <div className="flex lg:flex-row flex-col items-start w-full gap-2 lg:gap-3">
                                        <div className="flex lg:size-6 size-4 items-center justify-center rounded-full bg-[#14AD4D]">
                                          <Check
                                            className=" size-3 text-white"
                                            strokeWidth={3}
                                          />
                                        </div>
                                        <div className="flex flex-col gap-1 w-full text-left">
                                          <h3 className="lg:text-[16px] line-through text-[#1A202C] text-sm font-medium">
                                            {task.title}
                                          </h3> 
                                          <div className="flex flex-row items-center gap-1 lg:gap-2">     
                                          <Clock size={16} className="text-[#7F8493]" />
                                          <p className="lg:text-sm text-[12px] font-normal text-[#464B55]">Completed: </p>
                                             <span className="text-sm text-[#2D2F35] flex items-center gap-2 flex-wrap">
                                                <h6 className="text-xs text-[#202020CC]">
                                                  {`${formatFullDate(task.when)}`}
                                                </h6>
                                              </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div>                                    
                                      <CircleChevronDown
                                    strokeWidth={0.75}
                                    className="size-8 bg-white rounded-full text-[#7F8493] relative transition-transform duration-300 data-[state=open]:rotate-180"
                                  />
                                  </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="lg:p-4 p-2">
                                  <div className="flex">
                                      {renderAccordionContent(task)}
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                          </Accordion>
                        ) : <EmptyCompletedTodo />}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {payForMed && prescriptionId && (
        <MedicationPaymentModal
          prescription_id={prescriptionId}
          open={payForMed}
          onClose={() => setPayForMed(false)}
        />
      )}

      {requestRefill && reqPrescriptionId && reqMedicationId && (
        <MedicationRefillRequestModal
          prescription_id={reqPrescriptionId}
          medication_id={reqMedicationId}
          open={requestRefill}
          onClose={() => setRequestRefill(false)}
        />
      )}

      {payForRefill && refillPrescriptionId && refillMedicationId && (
        <MedicationRefillPaymentModal
          prescription_id={refillPrescriptionId}
          medication_id={refillMedicationId}
          open={payForRefill}
          onClose={() => setPayForRefill(false)}
        />
      )}
    </main>
  );
}
const ListItem = ({ Label, value }: { Label: string,value:string }) => {
  return (
    <li className="lg:text-[16px] text-[12px] text-[#1a202c] border-b border-b-[#0404040D] lg:py-[14px] py-[8px] px-[12px] lg:px-[20px] flex justify-between"><span className="text-[#464B55] font-normal">{Label}</span><span className="font-medium text-[#1A202C] "> {value}</span></li>
  );
};
  
const ListCointainer = ({title,description,list }: { title: string,description?: string,list: any }) => {
     return (
      <div>
        <div className="flex flex-col lg:gap-[8px] gap-[2px] lg:mb-[16px] mb-[8px]">
          <h3 className="lg:text-[16px] text-[12px] leading-[140%] lg:leading-[150%] text-[#1A202C] font-medium">{title}</h3>
          <p className="lg:text-[14px] text-[12px] leading-[140%] lg:leading-[140%] text-[#1A202C] font-normal">{description}</p>
        </div>
      <ol className=" bg-[#0404040D] rounded-[16px] overflow-hidden border border-[#0404040D]">
        { list.map((item: any, index: number) => (
          <ListItem key={index} Label={item.label} value={item.value} />
        ))}
        {/* <ListItem Label="Date" value={task.when ? formatDate(task.when) : "24 April, 2025"} />
        <ListItem Label="Time" value="Weight Loss Consultation" />
        <ListItem Label="Type" value="Weight Loss Consultation" />
        <ListItem Label="Status" value="Weight Loss Consultation" />
        <ListItem Label="Duration" value="Weight Loss Consultation" />
        <ListItem Label="Location" value="Weight Loss Consultation" />
        <ListItem Label="Fee" value="Weight Loss Consultation" />
        <ListItem Label="Progress" value="Weight Loss Consultation" />  
        <ListItem Label="Next step" value="Weight Loss Consultation" />
        <ListItem Label="Questions" value="Weight Loss Consultation" />
        <ListItem Label="Estimated Time" value="Weight Loss Consultation" />
        <ListItem Label="Required" value="Weight Loss Consultation" />
        <ListItem Label="Dosage" value="Weight Loss Consultation" />
        <ListItem Label="Supply" value="Weight Loss Consultation" />
        <ListItem Label="Total" value="Weight Loss Consultation" /> */}
      </ol>
    </div>
)};
 